/*
   The approved masters EPA holds for a registration.

   This lists candidates; it does not choose between them. Which master governs
   a review is resolved server-side when the pipeline runs — newest stamp on or
   before the printed date, escalating to needs_sme where that is not clean
   (DD-07-lite, api/services/version_match.py).

   `governingHint` mirrors that rule for display only, so the sheet can say
   where a run is expected to land before anyone pays for one. It is not the
   decision and must not become one: the server resolves against PPLS as it
   stands at run time, which need not match what this query returned.
*/

import { queryOptions } from "@tanstack/react-query"

import { http } from "@/lib/http"
import { qk } from "@/lib/query-keys"
import type { components } from "@/types/api.gen"

type DocumentsResponse = components["schemas"]["DocumentsResponse"]

/**
 * One approved master as PPLS publishes it.
 *
 * `stamp_date` (YYYYMMDD) is the field the governing rule compares against, and
 * it is absent from the published schema — the route returns it through this
 * type's open index signature. It is narrowed rather than asserted, so a
 * payload without it drops out of the ordering instead of corrupting it.
 */
export type LabelDocument = components["schemas"]["LabelDocument"]

/** A master that can be placed in time. An undated one cannot govern anything. */
interface DatedMaster {
  document: LabelDocument
  /** YYYYMMDD, so lexicographic order is chronological order. */
  stamp: string
}

function stampOf(doc: LabelDocument): string | null {
  const raw = doc["stamp_date"]
  return typeof raw === "string" && raw.length === 8 ? raw : null
}

/** The dated subset, newest first — the order the governing rule reads. */
export function datedNewestFirst(documents: readonly LabelDocument[]): DatedMaster[] {
  return documents
    .flatMap((document) => {
      const stamp = stampOf(document)
      return stamp === null ? [] : [{ document, stamp }]
    })
    .sort((a, b) => (a.stamp < b.stamp ? 1 : -1))
}

/**
 * Masters for a registration.
 *
 * EPA answers US addresses only, so this reaches it through the server's egress
 * proxy and fails for reasons unrelated to the label. No retry: a reviewer who
 * cannot reach EPA should be offered the upload path at once rather than
 * waiting out three attempts.
 */
export const mastersQuery = (regno: string) =>
  queryOptions({
    queryKey: qk.labels.documents(regno),
    queryFn: ({ signal }) =>
      http.get<DocumentsResponse>("/api/v1/labels/documents", { params: { regno }, signal }),
    enabled: regno.trim().length > 0,
    retry: false,
    staleTime: 5 * 60_000,
  })

/** Where the server's pick is expected to land, and whether it will be sure of it. */
export interface GoverningHint {
  document: LabelDocument
  /**
   * False when the server will fall back to the newest master and escalate to
   * needs_sme. Worth surfacing before submit: supplying the printed date is
   * usually all it takes to avoid.
   */
  contemporaneous: boolean
  /** Why the pick is not contemporaneous. Absent when it is. */
  caveat?: string
}

/**
 * The master a run is expected to be judged against, for display.
 *
 * Carries both of the server's fallbacks: with no printed date, or with every
 * master postdating the label, it takes the newest and escalates rather than
 * guessing. Reporting "nothing governs" in those cases would describe an
 * outcome the server never produces.
 *
 * Returns null only when no master carries a stamp date at all.
 */
export function governingHint(
  documents: readonly LabelDocument[],
  printedOn: string | null,
): GoverningHint | null {
  const dated = datedNewestFirst(documents)
  const newest = dated[0]
  if (!newest) return null

  if (!printedOn) {
    return {
      document: newest.document,
      contemporaneous: false,
      caveat:
        "Without the printed date the review uses the newest master and asks a specialist to confirm it.",
    }
  }

  const bound = printedOn.replaceAll("-", "")
  const contemporaneous = dated.find((d) => d.stamp <= bound)
  if (contemporaneous) return { document: contemporaneous.document, contemporaneous: true }

  return {
    document: newest.document,
    contemporaneous: false,
    caveat:
      "Every master on record was approved after this label was printed, so the review uses the newest and asks a specialist to confirm it.",
  }
}
